import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BusinessProvider } from '../context/BusinessContext';
import BusinessDetailsForm from '../components/BusinessDetailsForm';
import OfferingsManager from '../components/OfferingsManager';
import RedemptionsView from '../components/RedemptionsView';
import { apiService, BusinessUser } from '../services/api';
import './Dashboard.css';

type TabType = 'dashboard' | 'business-details' | 'offerings' | 'redemptions';

const BusinessAdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('business-details');
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const data = await apiService.getBusinessUsers();
        setUsers(data.users || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    if (activeTab === 'dashboard') {
      fetchUsers();
    }
  }, [activeTab]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <BusinessProvider>
      <div className="dashboard-container">
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-brand">
              <img src="/logo.png" alt="LinkPerks" className="header-logo" />
            </div>
            <div className="header-actions">
              <div className="user-info">
                <span className="user-email">{user?.email}</span>
                <span className="user-role">Business Admin</span>
              </div>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </header>

        <nav className="dashboard-nav">
          <div className="nav-content">
            <button
              className={`nav-tab ${activeTab === 'business-details' ? 'active' : ''}`}
              onClick={() => setActiveTab('business-details')}
            >
              🏢 Business Details
            </button>
            <button
              className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              👥 Users
            </button>
            <button
              className={`nav-tab ${activeTab === 'offerings' ? 'active' : ''}`}
              onClick={() => setActiveTab('offerings')}
            >
              🎁 Offerings
            </button>
            <button
              className={`nav-tab ${activeTab === 'redemptions' ? 'active' : ''}`}
              onClick={() => setActiveTab('redemptions')}
            >
              ✅ Redemptions
            </button>
          </div>
        </nav>

        <main className="dashboard-main">
          <div className="dashboard-content">
            {activeTab === 'dashboard' && (
              <>
                <div className="info-card">
                  <div className="info-card-icon">👥</div>
                  <div className="info-card-content">
                    <h3>Your Users</h3>
                    <p>View customers who have registered with your business.</p>
                  </div>
                  <span className="record-count">{users.length} users</span>
                </div>

                <div className="data-section">
                <div className="table-container">
                  {loadingUsers ? (
                    <div className="loading-state">Loading users...</div>
                  ) : users.length === 0 ? (
                    <div className="empty-state">No users have registered with your business yet</div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Name</th>
                          <th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={`${user.username}-${index}`}>
                            <td>
                              <div className="user-cell">
                                <div className="user-avatar">
                                  {(user.first_name || user.username).charAt(0).toUpperCase()}
                                </div>
                                <span>{user.username}</span>
                              </div>
                            </td>
                            <td className="name-cell">
                              {user.first_name && user.last_name 
                                ? `${user.first_name} ${user.last_name}`
                                : '-'}
                            </td>
                            <td>{formatDate(user.date_joined)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              </>
            )}

            {activeTab === 'business-details' && <BusinessDetailsForm />}
            {activeTab === 'offerings' && <OfferingsManager />}
            {activeTab === 'redemptions' && <RedemptionsView />}
          </div>
        </main>
      </div>
    </BusinessProvider>
  );
};

export default BusinessAdminDashboard;

